import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import api from '@/utils/api';
import Header from '@/components/Header';
import ServiceMeta from '@/components/ServiceMeta';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Youtube, Loader2, ArrowLeft, CheckCircle, Upload } from 'lucide-react';

const YoutubeServicePage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    channel_name: '',
    desired_subscribers: '',
    desired_views: '',
    category: '',
    special_requests: ''
  });
  const [paymentMethod, setPaymentMethod] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [paymentProof, setPaymentProof] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPaymentProof(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!paymentMethod) {
      toast.error('Choisissez une méthode de paiement');
      return;
    }
    
    // Si paiement direct, capture obligatoire
    if (paymentMethod === 'DIRECT' && !paymentProof) {
      toast.error('Veuillez uploader la capture de paiement');
      return;
    }
    
    setSubmitting(true);
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('channel_name', formData.channel_name);
      formDataToSend.append('desired_subscribers', formData.desired_subscribers);
      formDataToSend.append('desired_views', formData.desired_views);
      formDataToSend.append('category', formData.category);
      formDataToSend.append('special_requests', formData.special_requests);
      formDataToSend.append('payment_method', paymentMethod);
      formDataToSend.append('phone_number', phoneNumber);
      
      if (paymentProof) {
        formDataToSend.append('payment_proof', paymentProof);
      }
      
      await api.post('/service/youtube/purchase', formDataToSend, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setSubmitted(true);
      toast.success('✅ Commande enregistrée ! Contactez-nous sur WhatsApp');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur de soumission');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen pb-12">
        <ServiceMeta 
          title="Chaîne YouTube Monétisée - 1000 Abonnés + 4000h"
          description="Obtenez une chaîne YouTube monétisée prête à l'emploi avec 1000 abonnés et 4000 heures de visionnage pour activer vos revenus publicitaires."
          url="/monetisation-youtube"
        />
        <Header />
        
        <main className="pt-24 max-w-3xl mx-auto px-4">
          <Card className="glass border-zinc-800 text-center">
            <CardContent className="py-12">
              <CheckCircle className="mx-auto mb-4 text-green-500" size={64} />
              <h2 className="text-2xl font-bold text-white mb-3">
                Commande Enregistrée ! 🎉
              </h2>
              <p className="text-zinc-400 mb-6">
                Votre demande de Chaîne YouTube Monétisée a été reçue.
              </p>
              <div className="bg-zinc-900 rounded-lg p-6 mb-6">
                <p className="text-sm text-zinc-400 mb-3">
                  <strong className="text-white text-lg">📞 CONTACTEZ-MOI MAINTENANT :</strong>
                </p>
                <div className="space-y-2 text-white text-lg">
                  <p>📱 <strong>+243 978 475 357</strong></p>
                </div>
                <p className="text-zinc-400 text-sm mt-4">
                  Envoyez-moi un message WhatsApp pour confirmer votre paiement
                </p>
              </div>
              <div className="bg-blue-900/20 border border-blue-800 rounded-lg p-4 mb-6">
                <p className="text-sm text-blue-200">
                  <strong>Prochaines étapes :</strong>
                </p>
                <ul className="text-left text-sm text-blue-200 mt-2 space-y-1">
                  <li>✓ Commande enregistrée</li>
                  <li>⏳ Contactez-moi sur WhatsApp</li>
                  <li>⏳ Préparation de votre chaîne (24-48h)</li>
                  <li>⏳ Livraison des accès complets</li>
                </ul>
              </div>
              <Button onClick={() => navigate('/dashboard')} className="bg-[#E50914]">
                Retour au Dashboard
              </Button>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-12">
      <ServiceMeta 
        title="Chaîne YouTube Monétisée - 1000 Abonnés + 4000h"
        description="Obtenez une chaîne YouTube monétisée prête à l'emploi avec 1000 abonnés et 4000 heures de visionnage pour activer vos revenus publicitaires."
        url="/monetisation-youtube"
      />
      <Header />
      
      <main className="pt-24 max-w-3xl mx-auto px-4">
        <Button 
          onClick={() => navigate('/dashboard')}
          variant="ghost"
          className="mb-6"
        >
          <ArrowLeft className="mr-2" size={18} />
          Retour
        </Button>

        <Card className="glass border-zinc-800">
          <CardHeader>
            <CardTitle className="flex items-center gap-3 text-2xl">
              <Youtube className="text-red-500" size={32} />
              Acheter Chaîne YouTube Monétisée - 250$
            </CardTitle>
            <p className="text-zinc-400 text-sm mt-2">
              Chaîne prête à gagner avec monétisation activée
            </p>
          </CardHeader>
          
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Informations Chaîne */}
              <div>
                <Label>Nom complet de la chaîne souhaité *</Label>
                <Input
                  value={formData.channel_name}
                  onChange={(e) => setFormData({...formData, channel_name: e.target.value})}
                  required
                  placeholder="Ex: Ma Chaîne Musique Congo"
                  className="bg-zinc-900 border-zinc-800 text-white"
                />
                <p className="text-xs text-zinc-500 mt-1">Le nom exact que vous voulez pour votre chaîne</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Nombre d'abonnés souhaités *</Label>
                  <Input
                    type="number"
                    value={formData.desired_subscribers}
                    onChange={(e) => setFormData({...formData, desired_subscribers: e.target.value})}
                    required
                    placeholder="Ex: 1000"
                    className="bg-zinc-900 border-zinc-800 text-white"
                  />
                </div>

                <div>
                  <Label>Nombre de vues souhaitées *</Label>
                  <Input
                    type="number"
                    value={formData.desired_views}
                    onChange={(e) => setFormData({...formData, desired_views: e.target.value})}
                    required
                    placeholder="Ex: 10000"
                    className="bg-zinc-900 border-zinc-800 text-white"
                  />
                </div>
              </div>

              <div>
                <Label>Catégorie de la chaîne *</Label>
                <Select value={formData.category} onValueChange={(value) => setFormData({...formData, category: value})}>
                  <SelectTrigger className="bg-zinc-900 border-zinc-800 text-white">
                    <SelectValue placeholder="Choisir une catégorie" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    <SelectItem value="Musique">Musique</SelectItem>
                    <SelectItem value="Divertissement">Divertissement</SelectItem>
                    <SelectItem value="Gaming">Gaming</SelectItem>
                    <SelectItem value="Education">Éducation</SelectItem>
                    <SelectItem value="Sport">Sport</SelectItem>
                    <SelectItem value="News">Actualités</SelectItem>
                    <SelectItem value="Autre">Autre</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Demandes spéciales (optionnel)</Label>
                <Textarea
                  value={formData.special_requests}
                  onChange={(e) => setFormData({...formData, special_requests: e.target.value})}
                  placeholder="Ex: Langue française, thématique spécifique..."
                  className="bg-zinc-900 border-zinc-800 text-white"
                  rows={3}
                />
              </div>

              {/* Options de Paiement */}
              <div className="bg-blue-900/20 border border-blue-800 rounded-lg p-6">
                <h3 className="font-bold text-white text-lg mb-4">💰 Choisir Mode de Paiement</h3>
                
                <div className="space-y-4">
                  <Label>Méthode de paiement *</Label>
                  <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                    <SelectTrigger className="bg-zinc-900 border-zinc-800 text-white">
                      <SelectValue placeholder="Choisir..." />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800">
                      <SelectItem value="ORANGE_MONEY">Orange Money (via le site)</SelectItem>
                      <SelectItem value="AIRTEL_MONEY">Airtel Money (via le site)</SelectItem>
                      <SelectItem value="M_PESA">M-Pesa (via le site)</SelectItem>
                      <SelectItem value="DIRECT">Paiement Direct (envoi manuel)</SelectItem>
                    </SelectContent>
                  </Select>

                  {paymentMethod === 'DIRECT' && (
                    <div className="bg-green-900/20 border border-green-800 rounded-lg p-4 space-y-3">
                      <p className="text-green-200 font-semibold">💵 Envoyez 250$ à ce numéro :</p>
                      <div className="space-y-2 text-white">
                        <p className="text-lg">📱 <strong>+243 978 475 357</strong></p>
                      </div>
                      <p className="text-sm text-green-200">Puis uploadez la capture d'écran ci-dessous :</p>
                      
                      <div>
                        <Label>Capture d'écran de paiement *</Label>
                        <label className="flex flex-col items-center justify-center w-full p-6 border-2 border-dashed border-green-700 rounded-lg cursor-pointer hover:border-green-500 transition-colors bg-zinc-900">
                          <Upload className="text-green-500 mb-2" size={32} />
                          <p className="text-sm text-green-200">
                            {paymentProof ? paymentProof.name : 'Cliquez pour uploader la capture'}
                          </p>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleFileChange}
                            className="hidden"
                          />
                        </label>
                      </div>
                    </div>
                  )}

                  {paymentMethod && paymentMethod !== 'DIRECT' && (
                    <div>
                      <Label>Numéro de téléphone *</Label>
                      <Input
                        type="tel"
                        value={phoneNumber}
                        onChange={(e) => setPhoneNumber(e.target.value)}
                        placeholder="+243..."
                        required
                        className="bg-zinc-900 border-zinc-800 text-white"
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Important */}
              <div className="bg-yellow-900/20 border border-yellow-800 rounded-lg p-4">
                <strong className="flex items-center gap-2 text-yellow-200">
                  ⚠️ Important :
                </strong>
                <ul className="mt-2 space-y-1 ml-4 list-disc text-sm text-yellow-200">
                  <li>Chaîne YouTube 100% monétisée et vérifiée</li>
                  <li>Monétisation active garantie</li>
                  <li>Livraison sous 24-48h</li>
                  <li className="font-bold text-base">📞 CONTACTEZ-MOI APRÈS AVOIR PAYÉ</li>
                  <li>WhatsApp : <strong>+243 978 475 357</strong></li>
                </ul>
              </div>

              <Button
                type="submit"
                disabled={submitting}
                className="w-full bg-gradient-to-r from-red-600 to-red-800 hover:opacity-90 py-6 text-lg font-semibold"
              >
                {submitting ? <Loader2 className="animate-spin" /> : 'COMMANDER MAINTENANT (250$)'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default YoutubeServicePage;
